/**
 * Résumé de maîtrise du parcours pour l’onglet Suivi.
 */

import {
  PARCOURS_NODE_ORDER,
  STAGE_LABELS,
  getNodeDef
} from '../data/parcoursTree'
import { getNodeShortLabel, getNodeSubtitle } from './nodeCriteria'

/** Un nœud est maîtrisé si 3 succès consécutifs, ou 5 succès au total avec moyenne ≥ 70 %. */
export function isNodeMastered(nodeStats) {
  if (!nodeStats) return false
  const hasConsecutiveSuccesses = (nodeStats.consecutiveSuccesses ?? 0) >= 3
  const hasTotalSuccesses =
    (nodeStats.successes ?? 0) >= 5 && (nodeStats.averageScore ?? 0) >= 70
  return hasConsecutiveSuccesses || hasTotalSuccesses
}

export function getMasteryLevelLabel(masteryLevel) {
  switch (masteryLevel) {
    case 'beginner':
      return 'Débutant'
    case 'intermediate':
      return 'Intermédiaire'
    case 'advanced':
      return 'Avancé'
    default:
      return 'Débutant'
  }
}

export function getProgressionModeLabel(progressionMode) {
  switch (progressionMode) {
    case 'functions':
      return 'Intuition'
    case 'qcm':
      return 'Précision'
    case 'full':
      return 'Maîtrise'
    default:
      return 'Intuition'
  }
}

/** Phrase claire pour l’élève (ce qu’on lui demande de faire dans ce mode). */
export function getProgressionModeExplanation(progressionMode) {
  switch (progressionMode) {
    case 'functions':
      return 'Identifie la fonction (tonique, sous-dominante, dominante).'
    case 'qcm':
      return 'Choisis le bon accord parmi les propositions.'
    case 'full':
      return 'Saisis l’accord complet (degré, renversement, cadence).'
    default:
      return 'Identifie la fonction (tonique, sous-dominante, dominante).'
  }
}

/** Dernier nœud débloqué selon l’ordre du parcours (même règle que CampaignMap). */
export function getLastUnlockedNodeId(progress) {
  if (!progress?.unlockedNodes?.length) return null
  for (let i = PARCOURS_NODE_ORDER.length - 1; i >= 0; i--) {
    if (progress.unlockedNodes.includes(PARCOURS_NODE_ORDER[i])) {
      return PARCOURS_NODE_ORDER[i]
    }
  }
  return null
}

/**
 * Prochaine étape pour valider le nœud — une seule phrase d’objectif, sans jargon « Alternative ».
 */
export function getNextUnlockHint(nodeStats) {
  if (!nodeStats) {
    return 'Pour débloquer la suite : réussis 3 exercices d’affilée à 70 % ou plus.'
  }
  if (isNodeMastered(nodeStats)) {
    return 'Cette étape est validée. Ouvre le parcours pour voir la suite.'
  }

  const consecutive = nodeStats.consecutiveSuccesses ?? 0
  const successes = nodeStats.successes ?? 0
  const avg = nodeStats.averageScore ?? 0
  const attempts = nodeStats.attempts ?? 0

  if (consecutive > 0 && consecutive < 3) {
    const left = 3 - consecutive
    return left === 1
      ? 'Plus qu’1 exercice réussi (≥ 70 %) d’affilée pour valider cette étape.'
      : `Plus que ${left} exercices réussis (≥ 70 %) d’affilée pour valider cette étape.`
  }

  // Début de nœud ou série cassée : énoncer l’objectif principal clairement
  if (attempts === 0 || consecutive === 0) {
    if (successes >= 5 && avg < 70) {
      return 'Objectif : remonter ta moyenne à 70 % ou plus sur cette étape.'
    }
    if (successes > 0 && successes < 5) {
      return `Objectif : 3 réussites d’affilée (≥ 70 %), ou encore ${5 - successes} réussite${5 - successes > 1 ? 's' : ''} pour viser une moyenne ≥ 70 %.`
    }
    return 'Objectif : réussir 3 exercices d’affilée à 70 % ou plus pour valider cette étape.'
  }

  if (successes >= 5 && avg < 70) {
    return 'Objectif : remonter ta moyenne à 70 % ou plus sur cette étape.'
  }

  return 'Continue les exercices de cette étape pour la valider et débloquer la suite.'
}

/**
 * @param {object|null} progress — document userProgress Firestore
 */
export function buildParcoursProgressSummary(progress) {
  const unlockedNodes = progress?.unlockedNodes || []
  const nodeStatsMap = progress?.nodeStats || {}
  const totalNodes = PARCOURS_NODE_ORDER.length
  const unlockedCount = unlockedNodes.filter((id) => PARCOURS_NODE_ORDER.includes(id)).length
  const activeNodeId = getLastUnlockedNodeId(progress)
  const activeStats = activeNodeId ? nodeStatsMap[activeNodeId] : null
  const def = activeNodeId ? getNodeDef(activeNodeId) : null
  const stage = def?.stage ?? 1
  const masteryLevel = activeStats?.masteryLevel || 'beginner'
  const progressionMode = activeStats?.progressionMode || 'functions'

  return {
    unlockedCount,
    totalNodes,
    percentUnlocked: totalNodes > 0 ? Math.round((unlockedCount / totalNodes) * 100) : 0,
    activeNodeId,
    activeNodeLabel: activeNodeId ? getNodeShortLabel(activeNodeId) : null,
    activeNodeSubtitle: activeNodeId ? getNodeSubtitle(activeNodeId) : null,
    stageLabel: STAGE_LABELS[stage] ?? `Étage ${stage}`,
    masteryLevel,
    progressionMode,
    masteryLabel: getMasteryLevelLabel(masteryLevel),
    modeLabel: getProgressionModeLabel(progressionMode),
    modeExplanation: getProgressionModeExplanation(progressionMode),
    averageScore: activeStats ? Math.round(activeStats.averageScore ?? 0) : null,
    attempts: activeStats?.attempts ?? 0,
    consecutiveSuccesses: activeStats?.consecutiveSuccesses ?? 0,
    successes: activeStats?.successes ?? 0,
    nextUnlockHint: getNextUnlockHint(activeStats),
    isMastered: activeStats ? isNodeMastered(activeStats) : false
  }
}
